import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Award, Sword, Coins, Skull, Sparkles, Download } from "lucide-react";
import Portrait from "@/components/Portrait";
import T from "@/components/T";

export default function TrophyView({ ch }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => { api.profile().then(setData); }, []);

  const exportCard = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#050505",
        cacheBust: true,
      });
      const link = document.createElement("a");
      link.download = `grimwalker-trophy-${(data?.name || "hero").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Trophy card saved");
    } catch (e) {
      toast.error("Could not forge the card");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="panel p-4 text-center text-[#666] italic text-sm">Polishing the display case...</div>;

  const stats = [
    { Icon: Sword,     label: "Enemies",   value: data.counters.enemies || 0, color: "#8A0303" },
    { Icon: Coins,     label: "Sold",      value: data.counters.sold || 0,    color: "#B89C3A" },
    { Icon: Sparkles,  label: "Crafted",   value: data.counters.crafted || 0, color: "#4A7BC7" },
    { Icon: Skull,     label: "Steps",     value: data.counters.steps || 0,   color: "#8c8c8c" },
    { Icon: Award,     label: "Arena Wins",value: data.arena_wins || 0,       color: "#E5C34A" },
  ];

  return (
    <div className="space-y-2" data-testid="trophy-view">
      <button
        data-testid="trophy-export"
        onClick={exportCard}
        disabled={busy}
        className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-40 flex items-center justify-center gap-2"
      >
        <Download className="w-3 h-3" strokeWidth={1.5} />
        {busy ? <T>Forging Card…</T> : <T>Share Trophy Card</T>}
      </button>

      <div ref={cardRef} className="space-y-2 p-2" style={{ background: "#050505" }} data-testid="trophy-card">
        <div className="panel p-4 flex items-center gap-3 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background:
                "radial-gradient(60% 100% at 100% 0%, rgba(229,90,0,0.18), transparent 60%), radial-gradient(80% 80% at 0% 100%, rgba(138,3,3,0.18), transparent 60%)",
            }}
          />
          <div className="relative z-10 flex items-center gap-3 w-full">
            <Portrait name={data.name} cls={data.class} size={56} active_title={data.active_title} appearance={data.appearance} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg text-[#E2DFD8] truncate">{data.name}</div>
              <div className="font-mono text-[10px] text-[#8c8c8c] uppercase tracking-widest">
                {data.class} · Lv {data.level}
              </div>
              {data.active_title && (
                <div className="font-heading text-[10px] tracking-widest uppercase text-[#B89C3A] mt-1">
                  « {data.active_title.replace(/_/g, " ")} »
                </div>
              )}
            </div>
            <div className="font-heading text-[9px] tracking-[0.3em] uppercase text-[#666] text-right leading-tight">
              GRIMWALK<br/>Trophy Card
            </div>
          </div>
        </div>

        <div className="panel p-3 grid grid-cols-5 gap-1" data-testid="trophy-counters">
          {stats.map(s => {
            const Icon = s.Icon;
            return (
              <div key={s.label} className="panel-inner p-2 flex flex-col items-center gap-1">
                <Icon className="w-3 h-3" style={{ color: s.color }} strokeWidth={1.5} />
                <span className="font-mono text-sm text-[#E2DFD8]">{s.value}</span>
                <span className="font-heading text-[8px] tracking-widest uppercase text-[#666]">
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="panel p-3">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2">
            Titles Earned ({data.titles.length})
          </div>
          {data.titles.length === 0 && (
            <p className="italic text-[#666] text-xs">No titles yet. Adventure calls.</p>
          )}
          <div className="flex flex-wrap gap-1">
            {data.titles.map(t => (
              <span
                key={t}
                className={`px-2 py-1 border font-heading text-[10px] tracking-widest uppercase ${
                  data.active_title === t ? "border-[#E55A00] text-[#E55A00]" : "border-[#B89C3A] text-[#E5C34A]"
                }`}
              >
                {t.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>

        <div className="panel p-3">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2">
            Prized Relics
          </div>
          {(data.rarest_items || []).length === 0 && (
            <p className="italic text-[#666] text-xs">Nothing gleams yet in the display case.</p>
          )}
          <div className="space-y-1">
            {(data.rarest_items || []).map(i => (
              <div key={i.id} className={`panel-inner p-2 border-l-4 rarity-${i.rarity}`}>
                <div className={`font-heading text-sm rarity-${i.rarity}`}>{i.name}</div>
                <div className="font-mono text-[10px] text-[#8c8c8c] uppercase tracking-wider">
                  {i.slot} · {i.rarity} · {i.value}g
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center font-mono text-[8px] text-[#555] tracking-[0.3em] uppercase pt-1">
          grimwalker · dark fantasy MMORPG
        </div>
      </div>
    </div>
  );
}
